import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_URL } from "../config/API_URLS.tsx";
import { authenticatedFetch } from "../auth/RequestSigner.tsx";

export function ViewTickets() {
    const [loading, setLoading] = useState(false);   
    const [ticketData, setTicketData] = useState(null);
    const [documentAprendiz, setDocumentAprendiz] = useState("");
    const [aprendizData, setAprendizData] = useState(null);
    const { ticketId } = useParams();
    const roll = parseInt(localStorage.getItem('userRoll'));

    const handleLoad = useCallback(async () => {
        try {
            setLoading(true);
            const response = await authenticatedFetch(`${API_URL}/v1/Ticket/id/${ticketId}`, {
                method: 'get',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Error en la solicitud');
            }
            const data = await response.json();
            setTicketData(data);    
            setDocumentAprendiz(data.document);
        } catch (error) {
            console.error('Error:', error);
        }
    }, [ticketId]);

    const handleLoadUser = useCallback(async () => {
        if (documentAprendiz) {
            try {
                setLoading(true);
                const response = await authenticatedFetch(`${API_URL}/v1/aprendices/${documentAprendiz}`, {
                    method: 'get',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                if (!response.ok) {
                    throw new Error('Error en la solicitud');
                }
                const data = await response.json();
                setAprendizData(data);    
                setLoading(false); 
            } catch (error) {
                console.error('Error:', error);
            }        
        }
    }, [documentAprendiz]);

    useEffect(() => {
        handleLoad();
    }, [handleLoad]); 

    useEffect(() => {
        if (documentAprendiz) {
            handleLoadUser();
        }
    }, [documentAprendiz, handleLoadUser]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const [formData, setFormData] = useState({  
        status: '2',     
        subject: '',
        message: ''       
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const response = await authenticatedFetch(`${API_URL}/v1/ticket-response/${ticketId}`, {
                method: 'put',
                body: JSON.stringify(formData),
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Error en la solicitud');
            }
            setLoading(false);
            alert('La respuesta fue registrada exitosamente');
            window.location.reload();
        } catch (error) {
            setLoading(false);
            console.error('Error:', error);
            alert('Error al registrar la respuesta');
        }
    };

    function formatearFecha(fecha) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(fecha).toLocaleDateString(undefined, options);
    }

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8" data-testid="ticket-view-container">
            {loading && (
                <div className="flex items-center justify-center p-8 min-h-[300px]" data-testid="tickets-loading">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-700"></div>
                    <span className="ml-3 text-gray-700">Cargando información...</span>
                </div>
            )}

            {!loading && ticketData && aprendizData && (
                <div className="flex flex-col gap-6 tail" data-testid="ticket-content-wrapper">
                    <div className="flex justify-between items-center mb-2">
                        <h1 className="text-2xl font-bold text-gray-800" data-testid="ticket-page-title">
                            Detalle de Ticket #{ticketId}
                        </h1>
                        <Link 
                            to="/TicketsTable" 
                            className=" px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group"
                            data-testid="ticket-back-button"
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                            <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                            <span className="z-0">Volver a la lista</span>
                        </Link>
                    </div>

                    <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="ticket-content">
                        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-amber-100">
                            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                                <div data-testid="ticket-header-info">
                                    <h2 className="text-xl font-bold text-gray-800" data-testid="ticket-title">
                                        Incidencia de {aprendizData.name} {aprendizData.last_name}
                                    </h2>
                                    <p className="text-gray-600 text-sm mt-1" data-testid="ticket-date">
                                        Creado el {formatearFecha(ticketData.create_date)}
                                    </p>
                                </div>
                                <span 
                                    className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full
                                        ${ticketData.status === 1 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}
                                    data-testid="ticket-status-badge"
                                >
                                    {ticketData.status === 1 ? 'Nuevo' : 'Atendido'}
                                </span>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div data-testid="ticket-main-info">
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="ticket-description-title">
                                            Descripción del problema
                                        </h3>
                                        <p className="text-gray-700 whitespace-pre-wrap" data-testid="ticket-description">
                                            {ticketData.description}
                                        </p>
                                    </div>

                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="ticket-details-title">
                                            Detalles del ticket
                                        </h3>
                                        <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm" data-testid="ticket-details-list">
                                            <div>
                                                <span className="text-gray-500">Tipo de vehículo:</span>
                                                <p className="text-gray-800 font-medium" data-testid="ticket-vehicle-type-value">
                                                    {ticketData.vehicle_type}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Fecha:</span>
                                                <p className="text-gray-800 font-medium" data-testid="ticket-date-value">
                                                    {formatearFecha(ticketData.create_date)}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Correo:</span>
                                                <p className="text-gray-800 font-medium" data-testid="ticket-email-value">
                                                    {aprendizData.email}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Ficha:</span>
                                                <p className="text-gray-800 font-medium" data-testid="ticket-ficha-value">
                                                    {aprendizData.ficha}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Jornada:</span>
                                                <p className="text-gray-800 font-medium" data-testid="ticket-jornada-value">
                                                    Nocturna
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Estado:</span>
                                                <p className={`font-medium ${ticketData.status === 1 ? 'text-green-600' : 'text-amber-600'}`} data-testid="ticket-status-value">
                                                    {ticketData.status === 1 ? "Nuevo" : "Atendido"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6" data-testid="ticket-image-and-response">
                                    <div className="mb-4" data-testid="ticket-image-container">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="ticket-image-title">
                                            Imagen adjunta
                                        </h3>
                                        <div className="bg-gray-100 rounded-lg overflow-hidden">
                                            <img 
                                                className="w-full h-auto object-contain max-h-64 mx-auto" 
                                                alt="Imagen del ticket" 
                                                src={ticketData.photo} 
                                                data-testid="ticket-image"
                                            />
                                        </div>
                                    </div>

                                    {roll === 1 && (
                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-testid="ticket-response-container">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-3" data-testid="ticket-response-title">
                                                {ticketData.status === 1 ? "Responder ticket" : "Ticket respondido"}
                                            </h3>
                                            
                                            <form className="space-y-4" onSubmit={handleSubmit} data-testid="ticket-response-form">
                                                <div>
                                                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Asunto
                                                    </label>
                                                    <input 
                                                        className="w-full rounded-md py-2 px-3 border border-gray-300 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white disabled:bg-gray-100 disabled:text-gray-500 transition-colors duration-200" 
                                                        placeholder="Asunto de la respuesta" 
                                                        type="text"
                                                        name="subject" 
                                                        id="subject"
                                                        value={formData.subject}
                                                        onChange={handleInputChange}
                                                        disabled={ticketData.status === 2}
                                                        required
                                                        data-testid="ticket-response-subject"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Mensaje
                                                    </label>
                                                    <textarea 
                                                        className="w-full rounded-md py-2 px-3 border border-gray-300 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white disabled:bg-gray-100 disabled:text-gray-500 transition-colors duration-200"
                                                        placeholder="Escribe la respuesta detallada" 
                                                        rows="5" 
                                                        name="message" 
                                                        id="message"
                                                        value={formData.message}
                                                        onChange={handleInputChange}
                                                        disabled={ticketData.status === 2}
                                                        required
                                                        data-testid="ticket-response-message"
                                                    ></textarea>
                                                </div>
                                                
                                                <button  
                                                    type="submit" 
                                                    disabled={ticketData.status === 2}
                                                    className="w-full px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                                                    data-testid="ticket-response-submit"
                                                >
                                                    <div className={`absolute inset-0 ${ticketData.status === 2 ? 'bg-gray-400' : 'bg-gradient-to-t from-amber-300 to-amber-700'} transition-transform duration-700 ease-in-out`}></div>
                                                    <div className={`absolute inset-0 ${ticketData.status === 2 ? 'bg-gray-400' : 'bg-gradient-to-b from-amber-300 to-amber-700'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out`}></div>
                                                    <span className="z-10">
                                                        {ticketData.status === 1 ? "Enviar respuesta" : "Ticket ya respondido"}
                                                    </span>
                                                </button>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!loading && (!ticketData || !aprendizData) && (
                <div className="bg-white rounded-lg shadow-md p-6 text-center" data-testid="ticket-error">
                    <div className="text-amber-600 text-5xl mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">No se pudo cargar la información del ticket</h2>
                    <p className="text-gray-600 mb-6">Lo sentimos, pero no hemos podido encontrar la información solicitada.</p>
                    <Link 
                        to="/TicketsList" 
                        className="px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md inline-flex items-center justify-center relative overflow-hidden group"
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                        <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                        <span className="z-10 text-gray-900">Volver a la lista de tickets</span>
                    </Link>
                </div>
            )}
        </div>
    );
}
